import React, {useState, useEffect} from 'react';
import {Layout, Row, Col, Space, Button} from 'antd';
import {LogoutOutlined} from '@ant-design/icons';
import {useLocation} from 'react-router-dom';
import {createStyles} from 'antd-style';
import {configService, membershipService, useAuth} from "../../services";

const {Header} = Layout;

// 自定義按鈕樣式
const useStyle = createStyles(({prefixCls, css}) => ({
    linearGradientButton: css`
        &.${prefixCls}-btn-primary:not([disabled]):not(.${prefixCls}-btn-dangerous) {
            > span {
                position: relative;
            }

            &::before {
                content: '';
                background: linear-gradient(135deg, #6253e1, #04befe);
                position: absolute;
                inset: -1px;
                opacity: 1;
                transition: all 0.3s;
                border-radius: inherit;
            }

            &:hover::before {
                opacity: 0;
            }
        }
    `,
    headerUserInfo: css`
        .ant-space-compact {
            @media (max-width: 992px) {
                flex-direction: column-reverse !important;
                align-items: flex-end !important;
            }
            @media (max-width: 580px) {
                flex-direction: column-reverse !important;
                align-items: flex-end !important;
                gap: -2px;
            }
        }
    `,
    logoutCol: css`
        @media (max-width: 992px) {
            transform: translateY(10px) !important;
        }
        @media (max-width: 580px) {
            transform: translateY(0); /* 580px 取消位移 */
        }
    `,
    officialCountCol: css`
        @media (max-width: 992px) {
            transform: translateY(-10px) !important;
        }
        @media (max-width: 580px) {
            transform: translateY(0); /* 580px 取消位移 */
        }
    `,
    headerLogoContainer: css`
        display: flex;
        align-items: center;

        @media (max-width: 992px) {
            .header-system-title {
                transform: translateY(0);
            }
        }

        @media (max-width: 768px) {
            .header-logo {
                display: none; /* 隱藏 logo */
            }

            .header-system-title {
                margin-left: 0; /* 隱藏 logo 後調整間距 */
            }
        }
    `,
    headerRow: css`
        position: relative; /* 作為絕對定位參考 */
    `,
    headerRightOverlay: css`
        @media (max-width: 992px) {
            position: absolute;
            right: 0;
            /* 下移 16px，避免貼頂 */
            top: 16px;
            bottom: 0;
            display: flex;
            align-items: center; /* 使用 Flex 垂直置中 */
            z-index: 2;
            white-space: nowrap;
        }

    `,
}));

/**
 * CustomerHeader Component
 *
 * Renders the main application header with navigation.
 */
const CustomerHeader: React.FC = () => {
    const location = useLocation();
    const isLoginPage = location.pathname === '/login';
    const userAccount = localStorage.getItem('userAccount');
    const [officialCount, setOfficialCount] = useState<number>(
        Number(localStorage.getItem('officialCount')) || 0
    );// 正式會員數量
    const [sponsorCount, setSponsorCount] = useState<number>(
        Number(localStorage.getItem('sponsorCount')) || 0
    );// 贊助會員數量
    const {logout} = useAuth();
    const {styles} = useStyle();

    // 自動登出倒數計時狀態
    const [remainingTime, setRemainingTime] = useState<number>(0);

    // 設置倒數計時器
    useEffect(() => {
        if (isLoginPage) return;

        // 獲取會話超時時間
        const sessionTimeoutMs = configService.getSessionTimeoutMs();

        // 初始化剩餘時間
        const updateRemainingTime = () => {
            const loginTimeStr = localStorage.getItem('login_time');
            if (!loginTimeStr) return;

            const loginTime = parseInt(loginTimeStr, 10);
            const currentTime = Date.now();
            const elapsedMs = currentTime - loginTime;
            const remainingMs = Math.max(0, sessionTimeoutMs - elapsedMs);

            setRemainingTime(Math.floor(remainingMs / 1000)); // 轉換為秒
        };

        // 初次更新
        updateRemainingTime();

        // 每秒更新一次
        const intervalId = setInterval(updateRemainingTime, 1000);

        return () => clearInterval(intervalId);
    }, [isLoginPage]);

    // ✅ 改動2: 新增監聽會員數量更新事件
    useEffect(() => {
        if (isLoginPage) return;

        const handleMemberCountUpdate = async () => {
            // 呼叫 API 更新會員數量
            const counts = await membershipService.getMemberCounts();
            if (counts) {
                setOfficialCount(counts.officialCount);
                setSponsorCount(counts.sponsorCount);
                
                // 同步更新 localStorage
                localStorage.setItem('officialCount', counts.officialCount.toString());
                localStorage.setItem('sponsorCount', counts.sponsorCount.toString());
            }
        };

        // 監聽自定義事件
        window.addEventListener('memberCountUpdate', handleMemberCountUpdate);

        return () => {
            window.removeEventListener('memberCountUpdate', handleMemberCountUpdate);
        };
    }, [isLoginPage]);

    // 格式化剩餘時間為 MM:SS
    /* const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }; */
    // 格式化剩餘時間為 HH:MM:SS，如果小時為 0 則只顯示 MM:SS
    const formatTime2 = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes
                .toString()
                .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        }

        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
            .toString()
            .padStart(2, '0')}`;
    };

    return (
        <Header className="app-header">
            <Row justify="space-between" align="middle" style={{height: '100%'}} className={styles.headerRow}>
                <Col>
                    <Space align="center" className="header-padding-left">
                        <div className={`header-logo-container ${styles.headerLogoContainer}`}>
                            <img
                                src={`${process.env.PUBLIC_URL}/logo.png`}
                                alt="Taiwan Association of Machinery Industry"
                                className="header-logo"
                            />
                            <span className="header-system-title">會籍管理系統</span>
                        </div>
                    </Space>
                </Col>

                {!isLoginPage && (
                    <Col className={`header-padding-right ${styles.headerRightOverlay}`}>
                        <Row align="middle" gutter={6} className={styles.headerUserInfo}>
                            <Space.Compact>
                                <Col className={`d-flex align-items-center ${styles.officialCountCol}`}>
                              {
                                `
                                全會員共計：${officialCount+sponsorCount} 家，(含正式會員：${officialCount}家，贊助會員：${sponsorCount}家)
                                `
                              }
                            </Col>
                                <Col className={`d-flex align-items-center ${styles.logoutCol}`}>
                                    <Button
                                        type="primary"
                                        onClick={logout}
                                        className="logout-button"
                                    >
                                        歡迎，{userAccount} {remainingTime > 0 && ` (${formatTime2(remainingTime)})`}
                                        <LogoutOutlined className="logout-icon"/>
                                    </Button>
                                </Col>
                            </Space.Compact>
                        </Row>
                    </Col>
                )}
            </Row>
        </Header>
    );
};

export default CustomerHeader;
